/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  Configuration,
  GrantTokenDtoGrantTypeEnum,
  GrantTokenResponseDto,
  OidcApi,
  UserApi,
} from '@ink-ai/insight-service-sdk';
import { getStore } from '../reducers';
import { auth } from '../reducers/auth';
import { correction } from '../reducers/correction';

let refreshTokenPromise:
  | Promise<AxiosResponse<GrantTokenResponseDto, any>>
  | undefined;

export const getAccessToken = async (type: 'access' | 'id' = 'access') => {
  const store = getStore();
  const { accessToken, expiresAt, refreshToken, idToken } =
    store.getState().auth;
  const token = type === 'access' ? accessToken : idToken;
  // refresh token 1 minute before expire (now based on access token expiry)
  if (token && (expiresAt ?? 0) * 1000 > Date.now() + 1000 * 60) {
    return token;
  }
  // No refresh token: the session can't be renewed via refresh grant. Try a
  // silent re-auth (the IdP SSO session is usually still alive) before giving
  // up, so the user isn't bounced to the login page.
  if (!refreshToken) {
    if (await trySilentReauth()) {
      return getStoredToken(type);
    }
    throw Error('No refresh token available');
  }
  console.log('AccessToken expired, start refreshing');
  if (refreshTokenPromise === undefined) {
    refreshTokenPromise = getOidcApi().grantToken({
      grant_type: GrantTokenDtoGrantTypeEnum.RefreshToken,
      refresh_token: refreshToken,
    });
  }
  try {
    const res = await refreshTokenPromise;
    store.dispatch(auth.actions.tokenGranted(res.data));
    return type === 'access' ? res.data.access_token : res.data.id_token;
  } catch (error: any) {
    console.log('Refresh token failed');
    console.error(error);
    // Refresh grant failed (refresh token expired/revoked — OIDC returns 400
    // invalid_grant, not 401). Before signing out, try a silent re-auth using
    // the still-live IdP SSO session so the user stays logged in seamlessly.
    if (await trySilentReauth()) {
      return getStoredToken(type);
    }
    // Silent re-auth also failed: the IdP session is gone. Sign out so
    // usePrivateRoute redirects to /login instead of leaving callers (e.g. the
    // WebSocket reconnect loop) retrying forever with a dead token.
    store.dispatch(auth.actions.signOut());
    throw error;
  } finally {
    refreshTokenPromise = undefined;
  }
};

// Read the freshly-stored token after a successful (re)auth.
const getStoredToken = (type: 'access' | 'id'): string | undefined => {
  const { accessToken, idToken } = getStore().getState().auth;
  return type === 'access' ? accessToken : idToken;
};

/**
 * Force a token renewal regardless of the current expiry, and return the fresh
 * token (or undefined). Used by the 401 response interceptor: when a request is
 * rejected with 401 the locally-cached token is stale/revoked even though
 * `expiresAt` may still look valid, so we cannot trust the expiry check in
 * getAccessToken — we must renew unconditionally, then retry the request.
 *
 * Renewal order matches getAccessToken: refresh_token grant first (de-duped via
 * the shared refreshTokenPromise so concurrent 401s trigger a single refresh),
 * then a silent (prompt=none) re-auth, and only if both fail do we sign out.
 */
export const forceRenewToken = async (
  type: 'access' | 'id' = 'id',
): Promise<string | undefined> => {
  const store = getStore();
  const { refreshToken } = store.getState().auth;

  if (refreshToken) {
    if (refreshTokenPromise === undefined) {
      refreshTokenPromise = getOidcApi().grantToken({
        grant_type: GrantTokenDtoGrantTypeEnum.RefreshToken,
        refresh_token: refreshToken,
      });
    }
    try {
      const res = await refreshTokenPromise;
      store.dispatch(auth.actions.tokenGranted(res.data));
      return type === 'access' ? res.data.access_token : res.data.id_token;
    } catch (error) {
      console.error('401 interceptor: refresh_token grant failed', error);
      // fall through to silent re-auth
    } finally {
      refreshTokenPromise = undefined;
    }
  }

  if (await trySilentReauth()) {
    return getStoredToken(type);
  }

  // Both refresh and silent re-auth failed: session is truly gone.
  store.dispatch(auth.actions.signOut());
  return undefined;
};

// Lazy import to avoid a circular dependency (silentReauth imports this file).
const trySilentReauth = async (): Promise<boolean> => {
  try {
    const { silentReauth } = await import('./silentReauth');
    return await silentReauth();
  } catch (e) {
    console.error('Silent re-auth failed', e);
    return false;
  }
};

/**
 * The API base URL, with any trailing slash stripped.
 *
 * Custom-domain endpoints (e.g. https://api.bilingo.dev.int.bayer.com/) carry a
 * trailing slash. Concatenated with request paths ('/user/login', '/public',
 * the SDK's operation paths, ...) this yields a double slash '//', which on the
 * API Gateway custom domain is routed to the wrong resource and returns 401/404
 * (the execute-api domain tolerated it, so this only surfaced after switching to
 * the custom domain). The SDK does not normalize configuration.basePath, so we
 * strip it here — the single choke point every API client goes through.
 */
export const getApiBasePath = (): string =>
  (getStore().getState().app.appParam.ink_ai_api_endpoint || '').replace(
    /\/+$/,
    '',
  );

export const getApiConfig = async (): Promise<Configuration> => {
  return new Configuration({
    accessToken: await getAccessToken('id'),
    basePath: getApiBasePath(),
  });
};

export const getOidcApiConfig = (): Configuration => {
  return new Configuration({
    basePath: `${getApiBasePath()}/public`,
  });
};

export const getApi = async <T>(
  Api: new (conf: Configuration) => T,
): Promise<T> => {
  const store = getStore();
  if (!store.getState().auth.loggedIn && Api !== UserApi) {
    throw Error('User not logged in');
  }
  return new Api(await getApiConfig());
};

export const getOidcApi = () => {
  return new OidcApi(getOidcApiConfig());
};

export const makeRequest = async <T = any, D = any>(
  options: AxiosRequestConfig<D>,
) => {
  const store = getStore();
  try {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${store.getState().auth.idToken || store.getState().auth.accessToken}`,
    };
    options.baseURL = getApiBasePath();
    const reps = await axios<T>(options);
    return reps.data;
  } catch (error: any) {
    console.error(error);
    if (axios.isAxiosError(error)) {
      if (error.code === 'ERR_NETWORK') {
        store.dispatch(auth.actions.signOut());
        store.dispatch(correction.actions.clearAll());
      }
    }
    throw error;
  }
};
